import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { hasLink } from '@ngxp/rest';
import { UserFacade, UserResource } from '@schir-int-client/user-shared';
import {
	initialState,
	setVorgangStatusSuccessAction,
	VorgangFacade,
	VorgangLinkRel,
	VorgangResource,
} from '@schir-int-client/vorgang-shared';
import { StateResource } from '@schir-int-client/ngrx-helpers';
import { Observable, of, Subscription } from 'rxjs';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { UntypedFormControl, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import moment, { Moment } from 'moment/moment';
import { getId } from '../../../../test-helpers/src/lib/resource-helper';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { SCHIR_DATE_FORMATS } from '@schir-int-client/tech';

@Component({
	selector: 'schir-int-client-wiedervorlage-list',
	templateUrl: './wiedervorlage-list.component.html',
	styleUrls: ['./wiedervorlage-list.component.scss'],
	standalone: false,
	providers: [
		{
			provide: MAT_DATE_FORMATS,
			useValue: SCHIR_DATE_FORMATS,
		},
	],
})
export class WiedervorlageListComponent implements OnInit, OnDestroy {
	vorgaenge: Observable<StateResource<VorgangResource[]>>;

	vonDatumControl = new UntypedFormControl(initialState.datumsBereich.anfangsDatum, Validators.required);
	bisDatumControl = new UntypedFormControl(initialState.datumsBereich.endDatum, Validators.required);

	private subscriptions: Subscription[] = [];

	constructor(
		private userFacade: UserFacade,
		private vorgangFacade: VorgangFacade,
		private router: Router,
		private actionListener: ActionsSubject
	) {}

	ngOnInit(): void {
		const von = this.vonDatumControl.value
			? moment(this.vonDatumControl.value)
			: initialState.datumsBereich.anfangsDatum;
		const bis = this.bisDatumControl.value
			? moment(this.bisDatumControl.value)
			: initialState.datumsBereich.endDatum;

		this.vorgaenge = this.vorgangFacade.getVorgaengeInWiedervorlage(von, bis);

		this.vorgangFacade
			.getDatumsBereich()
			.pipe(first())
			.subscribe(value => {
				if (value.anfangsDatum.year() === 1970) {
					this.setVonDatum();
				} else {
					this.vonDatumControl.setValue(value.anfangsDatum);
					this.bisDatumControl.setValue(value.endDatum);
					this.vorgangFacade.refreshWiedervorlagen(value.anfangsDatum, value.endDatum);
				}
			});

		//TODO Eventuell im Rahmen von SCHIFF-5169 entfernen. Komponente muss dumm bleiben. Auf die Action sollte der vorgang.effect für alle Komponenten lauschen. Ist bereits vorbereitet
		this.subscriptions.push(
			this.actionListener.pipe(ofType(setVorgangStatusSuccessAction)).subscribe(() => {
				this.vorgangFacade.refreshWiedervorlagen(von, bis);
			})
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	/**
	 * Setzt das Von-Datum auf das Datum der ältesten Wiedervorlage
	 * @private
	 */
	private setVonDatum(): void {
		this.subscriptions.push(
			this.vorgaenge.pipe(first(value => value.loaded)).subscribe((value: StateResource<VorgangResource[]>) => {
				if (value.resource && value.resource.length > 0) {
					this.vonDatumControl.setValue(value.resource[0].wiedervorlageDatum);
				}
			})
		);
	}

	/**
	 * Initialisiert einen Reload der Wiedervorlagen, wenn über den Datepicker oder per manueller Eingabe ein anderes Datum ausgewählt wurde.
	 *
	 * @param event
	 */
	dateChosen(event: MatDatepickerInputEvent<Moment>): void {
		const von = this.vonDatumControl.value
			? moment(this.vonDatumControl.value)
			: initialState.datumsBereich.anfangsDatum;
		const bis = this.bisDatumControl.value
			? moment(this.bisDatumControl.value)
			: initialState.datumsBereich.endDatum;

		if (event.value && !this.vonDatumControl.errors && !this.bisDatumControl.errors) {
			if (von.isAfter(bis)) {
				return;
			}
			const dateFormat = 'YYYY-MM-DD';
			const datePattern = /^[12]\d{3}-[01]\d-[0123]\d$/;

			const vonString: string = von.format(dateFormat);
			const bisString: string = bis.format(dateFormat);

			if (datePattern.test(vonString) && datePattern.test(bisString)) {
				this.vorgangFacade.refreshWiedervorlagen(von, bis);
			}
		}
	}

	/**
	 * Achtung: ein Link Rechtspfleger scheint aktuell gar nicht gesetzt werden zu können.
	 * @param vorgang
	 */
	getRechtspfleger(vorgang: VorgangResource): Observable<UserResource> {
		if (hasLink(vorgang, VorgangLinkRel.RECHTSPFLEGER)) {
			return this.userFacade.getUserByUri(vorgang, VorgangLinkRel.RECHTSPFLEGER);
		}
		return of(null);
	}

	navigateToVorgang(vorgang: VorgangResource): void {
		this.vorgangFacade.setSelectedVorgang(vorgang);
		this.router.navigate([
			'/verfahren',
			getId(vorgang, VorgangLinkRel.VERFAHREN),
			{
				outlets: {
					vorgang: getId(vorgang),
				},
			},
		]);
	}

	loadFristenListe(): void {
		this.vorgangFacade.loadFristenListe();
	}
}
